// SPDX-License-Identifier: Apache-2.0

// tether-brain v2 — Dexie database wrapper.
//
// One DB, nine tables (v2). The base table list is fixed by Daedalus's
// storage-layout block in `architecture/SYSTEM.md`:
//   docs · embeddings_int8 · tfidf_postings · tfidf_vocab · edges
//   · adjacency_cache · yjs_snapshot · model_shards
// v2 adds: yjs_snapshot_log (per-doc append-only history; the existing
// yjs_snapshot row stays as the "latest" pointer for fast doc-open hydration).
//
// Schema versioning lives here. Bump `DB_VERSION` and add a new `db.version(n)`
// block with `.upgrade()` whenever a table changes shape — never edit a past
// version, ever. Dexie replays prior versions in order against the live IDB.

import Dexie, { type EntityTable } from 'dexie';
import type {
  VaultDoc,
  VaultEmbedding,
  TfidfPosting,
  TfidfVocab,
  VaultEdge,
  AdjacencyEntry,
  YjsSnapshot,
  ModelShard,
} from './schema';

/** Keep this name stable — changing it orphans every existing user's vault. */
export const DB_NAME = 'tether-brain-v2';

/**
 * Bump on schema change.
 *   v1 = initial release (forge-stream, 2026-04-26).
 *   v2 = add `yjs_snapshot_log` table for persistent snapshot history
 *        (Hephaestus-3, 2026-04-27). The existing `yjs_snapshot` row stays
 *        as the "latest" pointer for fast doc-open hydration.
 *   v3 = add `tombstones` table for hard-delete markers (Hephaestus-3,
 *        2026-04-27). One row per hard-deleted doc; future "this docId
 *        was here" lookups consult the table so the absence
 *        is intentional, not a silent 404. The row carries no PII — only
 *        the docId and the unix-ms `deleted_at`.
 */
export const DB_VERSION = 3;

/**
 * One row per Yjs snapshot append. Composite primary key `[docId+ts]` lets
 * us write append-only without ever overwriting prior rows. The per-`docId`
 * index supports the "list snapshots for this doc" query; the per-`ts`
 * index supports future "all snapshots in last 7 days" sweeps.
 *
 * `update` carries the FULL Yjs state-as-update (`Y.encodeStateAsUpdate`),
 * NOT a delta — `restoreSnapshot` rehydrates by applying it to a fresh
 * Y.Doc. Naming the field `update` keeps it interchangeable with the Yjs
 * "update" wire format.
 */
export interface YjsSnapshotLogRow {
  docId: string;
  /** unix ms */
  ts: number;
  /** `Y.encodeStateAsUpdate(doc)` at capture time. */
  update: Uint8Array;
  /** Optional human label like "before merge". */
  label?: string;
  /** `update.byteLength`, kept on the row so retention math can run without
   *  rehydrating the blob (IDB blob byteLength reads are not free). */
  byteLength: number;
}

/**
 * One row per hard-deleted doc — a local "this id is intentionally gone"
 * marker so future lookups can answer 404-with-reason instead of silent 404.
 * Carries no PII: the docId is the SPA-local UUID, and `deleted_at` is the
 * unix-ms timestamp of the deletion. Rows are append-only and never trimmed:
 * ~200 bytes per erasure means ~20 KB per 100 deletions — trivial IDB cost.
 */
export interface TombstoneRow {
  /** Same SPA-local UUID as the original `VaultDoc.id`. */
  docId: string;
  /** unix ms when the doc was hard-deleted. */
  deleted_at: number;
}

/**
 * Strongly-typed Dexie subclass. Declaring tables as `EntityTable<Row, 'pk'>`
 * makes `db.docs.put({...})` reject rows missing the primary key at compile
 * time, and `db.docs.get(id)` returns `Row | undefined` without a cast.
 */
export class TetherDB extends Dexie {
  declare docs: EntityTable<VaultDoc, 'id'>;
  declare embeddings_int8: EntityTable<VaultEmbedding, 'id'>;
  declare tfidf_postings: EntityTable<TfidfPosting, 'id'>;
  declare tfidf_vocab: EntityTable<TfidfVocab, 'term'>;
  declare edges: EntityTable<VaultEdge, 'id'>;
  declare adjacency_cache: EntityTable<AdjacencyEntry, 'id'>;
  declare yjs_snapshot: EntityTable<YjsSnapshot, 'docId'>;
  declare model_shards: EntityTable<ModelShard, 'id'>;
  /** v2 — per-doc append-only snapshot log. Composite pk `[docId+ts]` so
   *  Dexie's typed surface uses a tuple key; we only ever query through
   *  the `docId` index plus the `ts` sort, never via the raw pk type. */
  declare yjs_snapshot_log: EntityTable<YjsSnapshotLogRow, 'docId'>;
  /** v3 — hard-delete tombstones. One row per hard-deleted docId.
   *  Pk is `docId` (one tombstone per doc, idempotent overwrites OK). */
  declare tombstones: EntityTable<TombstoneRow, 'docId'>;

  constructor(name: string = DB_NAME) {
    super(name);

    // ─── v1 schema ──────────────────────────────────────────────────────────
    //
    // Index design rules:
    // - Primary key first, no `&` (Dexie infers uniqueness for the pk).
    // - `&field` = unique secondary index; `field` = non-unique; `[a+b]`
    //   = compound index (range-queryable on `a` then `b`).
    // - Bulk binary blobs (Int8Array, Uint8Array) are NOT indexed — IDB
    //   stores them as structured-cloned ArrayBufferViews and indexing them
    //   blows quota. We index foreign-key strings only.
    // - `updatedAt` is indexed everywhere we expect "recent N" queries
    //   (recents pane, idle-snapshot scheduler).
    this.version(1).stores({
      docs:
        'id, title, updatedAt, createdAt, ' +
        'front.sensitivity, front.type, front.slot, ' +
        '*front.tags',
      embeddings_int8:
        'id, docId, model, computedAt, [docId+model]',
      tfidf_postings:
        'id, term, docId, [term+docId]',
      tfidf_vocab:
        'term, df, idf',
      edges:
        'id, a, b, kind, w, computedAt, ' +
        '[a+kind], [b+kind]',
      adjacency_cache:
        'id, docId, k, computedAt, [docId+k]',
      yjs_snapshot:
        'docId, updatedAt',
      model_shards:
        'id, model, [model+shard]',
    });

    // ─── v2 schema ──────────────────────────────────────────────────────────
    //
    // Adds `yjs_snapshot_log`, an append-only per-doc history of Yjs updates.
    // The pre-existing `yjs_snapshot` table stays untouched: it remains the
    // "latest known state" pointer, hit on every doc-open to rehydrate the
    // Y.Doc fast. The log is the source of truth for the history viewer; the
    // single-row pointer is the source of truth for hydration.
    //
    // Pk is `[docId+ts]` — composite, append-only. We index `docId` (so
    // "all snapshots of this doc" range-scans cleanly) and `ts` (so future
    // "all snapshots in last 7 days" sweeps work without a full scan).
    // The `update` blob is intentionally NOT indexed — same rule as the v1
    // tables: bulk Uint8Array views blow IDB quota when indexed.
    //
    // Dexie's upgrade contract: declaring only the new tables on `version(2)`
    // is enough — pre-existing tables carry forward unchanged. Re-declaring
    // them would have been a no-op but is omitted for clarity.
    this.version(2).stores({
      yjs_snapshot_log: '[docId+ts], docId, ts',
    });

    // ─── v3 schema ──────────────────────────────────────────────────────────
    //
    // Adds `tombstones`, a flat per-docId marker for hard deletion
    // (Hephaestus-3, 2026-04-27). Row is minimal: `docId` (pk) +
    // `deleted_at` (indexed for "all deletions in last N days" sweeps).
    // Pre-existing tables carry forward unchanged per Dexie's upgrade
    // contract.
    this.version(3).stores({
      tombstones: 'docId, deleted_at',
    });
  }
}

// ─── module-level singleton ─────────────────────────────────────────────────
//
// We own this rather than letting callers `new TetherDB()` themselves —
// every component in the SPA must hit the same IDB connection or Dexie's
// liveQuery / observable hooks split-brain on cross-tab updates.

let _db: TetherDB | null = null;

export function getDB(): TetherDB {
  if (!_db) _db = new TetherDB();
  return _db;
}

/** Test-only: dispose and forget the singleton so a fresh DB can be opened
 *  against a different name (used by `dexie-schema.test.ts`). */
export function _resetDBForTests(): void {
  if (_db) _db.close();
  _db = null;
}

/** Open a named DB — escape hatch for tests and for the future multi-vault
 *  feature. Production code should always use `getDB()`. */
export function openNamedDB(name: string): TetherDB {
  return new TetherDB(name);
}
